// ============================================================
// pages/AuthPage.jsx — Login / Signup Page
// ============================================================
// Firebase Auth: Email/password + Google sign-in

import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";

export default function AuthPage({ onSuccess, showToast }) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "signup" | "reset"
  const [form, setForm] = useState({
    email: "", password: "", displayName: "", phone: "", confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "login") {
        await signIn(form.email, form.password);
        showToast("Welcome back!", "success");
        onSuccess();
      } else if (mode === "signup") {
        if (form.password !== form.confirmPassword) {
          setError("Passwords don't match");
          setLoading(false);
          return;
        }
        if (form.password.length < 6) {
          setError("Password must be at least 6 characters");
          setLoading(false);
          return;
        }
        await signUp(form.email, form.password, form.displayName, form.phone);
        showToast("Account created! Welcome to Chaitimecrunch!", "success");
        onSuccess();
      } else if (mode === "reset") {
        await resetPassword(form.email);
        showToast("Password reset email sent! Check your inbox.", "success");
        setMode("login");
      }
    } catch (err) {
      const msg = err.code === "auth/user-not-found" ? "No account with this email"
        : err.code === "auth/wrong-password" ? "Incorrect password"
        : err.code === "auth/email-already-in-use" ? "Email already registered"
        : err.code === "auth/invalid-email" ? "Invalid email address"
        : err.message || "Something went wrong";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 440, margin: "2rem auto" }}>
      <div className="card" style={{ padding: "2.5rem" }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.8rem" }}>
            {mode === "login" ? "Welcome Back" : mode === "signup" ? "Join Chaitimecrunch" : "Reset Password"}
          </h2>
          <p style={{ color: "var(--cocoa)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
            {mode === "login" ? "Sign in to your account"
              : mode === "signup" ? "Create an account to start ordering"
              : "Enter your email to reset your password"}
          </p>
        </div>

        {error && (
          <div style={{
            background: "#FFF3F3", border: "1px solid #FFCDD2", borderRadius: 8,
            padding: "0.75rem 1rem", marginBottom: "1.5rem", color: "#C62828",
            fontSize: "0.88rem",
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {mode === "signup" && (
            <>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" type="text" required
                  value={form.displayName} onChange={(e) => update("displayName", e.target.value)}
                  placeholder="Your name" />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" type="tel"
                  value={form.phone} onChange={(e) => update("phone", e.target.value)}
                  placeholder="+230 5XXX XXXX" />
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" required
              value={form.email} onChange={(e) => update("email", e.target.value)}
              placeholder="you@email.com" />
          </div>

          {mode !== "reset" && (
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" required
                value={form.password} onChange={(e) => update("password", e.target.value)}
                placeholder="••••••••" />
            </div>
          )}

          {mode === "signup" && (
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" required
                value={form.confirmPassword} onChange={(e) => update("confirmPassword", e.target.value)}
                placeholder="••••••••" />
            </div>
          )}

          <button className="btn btn-primary" type="submit"
            style={{ width: "100%", marginTop: "0.5rem", justifyContent: "center" }}
            disabled={loading}>
            {loading ? "Please wait..."
              : mode === "login" ? "Sign In"
              : mode === "signup" ? "Create Account"
              : "Send Reset Link"}
          </button>
        </form>

        <div style={{ marginTop: "1.5rem", textAlign: "center", fontSize: "0.88rem" }}>
          {mode === "login" && (
            <>
              <button onClick={() => setMode("reset")} style={{
                background: "none", border: "none", color: "var(--chai)",
                cursor: "pointer", fontSize: "0.85rem", marginBottom: "0.5rem", display: "block", width: "100%",
              }}>
                Forgot password?
              </button>
              <span style={{ color: "var(--cocoa)" }}>Don't have an account? </span>
              <button onClick={() => setMode("signup")} style={{
                background: "none", border: "none", color: "var(--chai)",
                cursor: "pointer", fontWeight: 600, fontSize: "0.88rem",
              }}>
                Sign Up
              </button>
            </>
          )}
          {mode === "signup" && (
            <>
              <span style={{ color: "var(--cocoa)" }}>Already have an account? </span>
              <button onClick={() => setMode("login")} style={{
                background: "none", border: "none", color: "var(--chai)",
                cursor: "pointer", fontWeight: 600, fontSize: "0.88rem",
              }}>
                Sign In
              </button>
            </>
          )}
          {mode === "reset" && (
            <button onClick={() => setMode("login")} style={{
              background: "none", border: "none", color: "var(--chai)",
              cursor: "pointer", fontWeight: 600, fontSize: "0.88rem",
            }}>
              ← Back to Sign In
            </button>
          )}
        </div>
      </div>
    </div>
  );
}